function slideHasInteractiveMedia(slide) {
  if (!slide) return false;

  return Boolean(slide.querySelector("deferred-media, product-model"));
}

function getActiveSwiperSlide(swiper) {
  if (!swiper) return null;

  const activeIndex = swiper.params.loop
    ? swiper.realIndex
    : swiper.activeIndex;

  return (
    swiper.el?.querySelector(".swiper-slide-active") ||
    swiper.slides?.[activeIndex] ||
    null
  );
}

function syncMediaArrowsInteractiveState(section) {
  if (!section) return;

  const mediaList = section.querySelector(".js-media-list");
  const arrowsWrap = section.querySelector(".product__media-arrows");
  if (!mediaList || !arrowsWrap) return;

  const swiper = mediaList.swiper;
  if (!swiper) return;

  const isInteractive = slideHasInteractiveMedia(getActiveSwiperSlide(swiper));
  arrowsWrap.classList.toggle("is-media-interactive", isInteractive);
}

function initProductPage(section) {
  if (!section || !section.classList.contains("product-section")) {
    return;
  }

  const initMainSlider = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const sliderEl = section.querySelector(".js-media-list");
    if (!sliderEl) return;

    const desktopType = sliderEl.dataset?.desktopType || "slider";
    const mobileType = sliderEl.dataset?.mobileType || "slider";
    const hasDesktopZoom = sliderEl.dataset?.hasDesktopZoom === "true";
    const navPrev = sliderEl.querySelector(".swiper-button-prev");
    const navNext = sliderEl.querySelector(".swiper-button-next");
    const mediaBox = sliderEl.closest(".product__media");
    const thumbsSlider = mediaBox?.querySelector(".js-media-sublist");
    const slideCountEl = sliderEl?.querySelector(".product__slider-nav-count");
    const paginationEl = mediaBox?.querySelector(".product__media-pagination");
    const paginationItems = paginationEl
      ? Array.from(
          paginationEl.querySelectorAll(".product__media-pagination-item")
        )
      : [];

    const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
    const isLoop = slideCount > 2 && slideCount <= 32;
    const speed = slideCount > 32 ? 0 : 800;
    const hasSliderNav = Boolean(navPrev && navNext);
    const isPaginationStacked = mobileType === "pagination_stacked";

    const syncPagination = (swiper) => {
      if (!paginationItems.length || !swiper) return;

      const activeIndex = swiper.params.loop
        ? swiper.realIndex
        : swiper.activeIndex;

      paginationItems.forEach((item, index) => {
        const isActive = index === activeIndex;
        item.classList.toggle("is-active", isActive);
        if (isActive) {
          item.setAttribute("aria-current", "true");
        } else {
          item.removeAttribute("aria-current");
        }
      });
    };

    paginationItems.forEach((item) => {
      if (item.dataset.paginationInit === "true") return;
      item.dataset.paginationInit = "true";
      item.addEventListener("click", () => {
        const swiper = sliderEl.swiper;
        const index = Number(item.dataset.slideIndex);
        if (!swiper || Number.isNaN(index)) return;

        if (swiper.params.loop) {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      });
    });

    // pagination_stacked (mobile): one full-frame slide per view, swiped
    // vertically. No loop, and the swipe / wheel is handed back to the page at
    // the first and last slide so the gallery never traps page scrolling.
    const getPaginationStackedConfig = () => ({
      direction: "vertical",
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      grabCursor: false,
      touchReleaseOnEdges: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      breakpoints: {},
    });

    const initSwiper = (mode) => {
      if (sliderEl.swiper) {
        sliderEl.swiper.destroy();
      }

      const swiperConfig = {
        slidesPerView: mobileType === "slider" ? "auto" : 1,
        spaceBetween: mobileType == "slider_previews" ? 8 : 0,
        autoHeight: false,
        loop: isLoop,
        direction: "horizontal",
        speed: speed,
        allowTouchMove: true,
        grabCursor: mobileType === "slider",
        watchSlidesProgress: true,
        mousewheel: {
          forceToAxis: true,
        },
        ...(hasSliderNav && navNext && navPrev
          ? {
              navigation: {
                nextEl: navNext,
                prevEl: navPrev,
              },
            }
          : {}),
        breakpoints: {
          990: {
            slidesPerView: desktopType === "slider" ? "auto" : 1,
            spaceBetween: desktopType == "slider_previews" ? 8 : 0,
            allowTouchMove: desktopType === "slider" ? true : !hasDesktopZoom,
            grabCursor: desktopType === "slider",
          },
        },
        on: {
          init: function () {
            if (slideCountEl) {
              slideCountEl.textContent = `1/${this.slides.length}`;
            }
            syncPagination(this);
          },
          slideChange: function () {
            window.pauseAllMedia(sliderEl);
            this.params.noSwiping = false;
            syncMediaArrowsInteractiveState(section);
            syncPagination(this);

            if (thumbsSlider?.swiper) {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              thumbsSlider.swiper.slideTo(activeIndex);
            }

            if (slideCountEl) {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              slideCountEl.textContent = `${activeIndex + 1}/${
                this.slides.length
              }`;
            }
          },
          slideChangeTransitionEnd: function () {
            const activeSlide = getActiveSwiperSlide(this);
            const model3D = activeSlide?.querySelector("model-viewer");
            const posterBtn3D = activeSlide?.querySelector(
              ".shopify-model-viewer-ui__button--poster"
            );
            if (model3D && posterBtn3D) {
              posterBtn3D.removeAttribute("hidden");
            }
            syncMediaArrowsInteractiveState(section);
          },
          touchStart: function () {
            const activeSlide = getActiveSwiperSlide(this);
            const model3D = activeSlide?.querySelector("model-viewer");
            if (model3D) {
              if (
                !model3D.classList.contains("shopify-model-viewer-ui__disabled")
              ) {
                this.params.noSwiping = true;
                this.params.noSwipingClass = "swiper-slide";
              } else {
                this.params.noSwiping = false;
              }
            }
          },
          touchEnd: function () {
            this.params.noSwiping = false;
          },
        },
      };

      if (thumbsSlider?.swiper) {
        swiperConfig.thumbs = {
          swiper: thumbsSlider.swiper,
          multipleActiveThumbs: false,
        };
      }

      if (mode === "mobile" && isPaginationStacked) {
        Object.assign(swiperConfig, getPaginationStackedConfig());
      }

      new Swiper(sliderEl, swiperConfig);
      syncMediaArrowsInteractiveState(section);
    };

    const destroySwiper = () => {
      if (sliderEl.swiper) {
        sliderEl.swiper.destroy();
      }
      sliderEl.querySelectorAll(".swiper-wrapper").forEach((wrapper) => {
        wrapper.removeAttribute("style");
      });
      sliderEl.querySelectorAll(".swiper-slide").forEach((slide) => {
        slide.removeAttribute("style");
      });
    };

    const hasDesktopSwiper =
      desktopType === "slider" || desktopType == "slider_previews";
    const hasMobileSwiper =
      mobileType === "slider" ||
      mobileType == "slider_previews" ||
      isPaginationStacked;

    let activeSwiperMode = null;

    const getSwiperMode = () => {
      if (window.innerWidth >= 990 && hasDesktopSwiper) return "desktop";
      if (window.innerWidth < 990 && hasMobileSwiper) return "mobile";
      return null;
    };

    const syncSwiperLayout = () => {
      const nextMode = getSwiperMode();

      if (nextMode === activeSwiperMode) return;

      activeSwiperMode = nextMode;

      if (nextMode) {
        initSwiper(nextMode);
      } else {
        destroySwiper();
      }
    };

    if (section._productMainSliderObserver) {
      section._productMainSliderObserver.disconnect();
      section._productMainSliderObserver = null;
    }

    // pagination_stacked needs its own mobile config (vertical, no loop), so it
    // always rebuilds the swiper when crossing the desktop / mobile breakpoint.
    if (hasDesktopSwiper && hasMobileSwiper && !isPaginationStacked) {
      activeSwiperMode = getSwiperMode();
      if (activeSwiperMode) initSwiper(activeSwiperMode);
    } else {
      syncSwiperLayout();

      section._productMainSliderObserver = new ResizeObserver(() => {
        syncSwiperLayout();
      });

      section._productMainSliderObserver.observe(section);
    }
  };

  const initSubSlider = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const sliderEl = section.querySelector(".js-media-sublist");
    if (!sliderEl) return;

    const desktopType = sliderEl.dataset?.desktopType || "slider";
    const mobileType = sliderEl.dataset?.mobileType || "slider";
    const hasDesktopSwiper = desktopType === "slider_previews";
    const hasMobileSwiper = mobileType === "slider_previews";
    const isRtlPage = document.documentElement.dir === "rtl";

    const initSwiper = () => {
      const thumbSlider = new Swiper(sliderEl, {
        spaceBetween: 8,
        slidesPerView: 4,
        loop: false,
        direction: "horizontal",
        allowTouchMove: true,
        watchSlidesProgress: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        autoHeight: false,
        slideToClickedSlide: true,
        on: {
          touchEnd: function (swiper) {
            let range = 5;
            let diff = (swiper.touches.diff = swiper.isHorizontal()
              ? swiper.touches.currentX - swiper.touches.startX
              : swiper.touches.currentY - swiper.touches.startY);
            if (diff < range || diff > -range) swiper.allowClick = true;
          },
        },
        breakpoints: {
          576: {
            slidesPerView: 6,
          },
          990: {
            slidesPerView: "auto",
            direction: "vertical",
            allowTouchMove: isRtlPage ? false : true,
          },
        },
      });

      return thumbSlider;
    };

    if (hasDesktopSwiper || hasMobileSwiper) {
      initSwiper();

      const syncStackedPreviewsSwiper = () => {
        if (!sliderEl.swiper) return;

        if (
          window.innerWidth >= 990 &&
          (desktopType === "framed_stacked" ||
            desktopType === "stacked" ||
            desktopType === "stacked_previews")
        ) {
          sliderEl.swiper.disable();
          return;
        }

        if (window.innerWidth >= 990 && hasDesktopSwiper) {
          sliderEl.swiper.enable();
          sliderEl.swiper.changeDirection("vertical", true);
        }

        if (window.innerWidth < 990 && hasMobileSwiper) {
          sliderEl.swiper.enable();
          sliderEl.swiper.changeDirection("horizontal", true);
        }
      };

      syncStackedPreviewsSwiper();

      const resizeObserver = new ResizeObserver(() => {
        syncStackedPreviewsSwiper();
      });

      resizeObserver.observe(section);
    }
  };

  const initZoomSlider = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const sectionBox = section.querySelector('[id^="MainProduct-"]');
    const sectionId = sectionBox?.dataset?.section;
    const sliderEl = document.querySelector(
      `[data-section-id='${sectionId}'].js-popup-slider`
    );
    if (!sliderEl) return;

    const buttonPrev = sliderEl.querySelector(".swiper-button-prev");
    const buttonNext = sliderEl.querySelector(".swiper-button-next");
    const slideCountEl = sliderEl?.querySelector(".product__slider-nav-count");

    const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
    const isLoop = slideCount > 2 && slideCount <= 32;
    const speed = slideCount > 32 ? 0 : 800;

    if (slideCount <= 1) return;

    const onMainSliderToggleClick = (event) => {
      if (!sliderEl.swiper) return;
      const zoomToggle = event.target.closest(".product__media-toggle");
      if (!zoomToggle) return;
      const mediaId = zoomToggle.dataset?.mediaId;
      if (!mediaId) return;
      const slideIndex = sliderEl.swiper.slides.findIndex((slide) =>
        slide.querySelector(`[data-media-id="${mediaId}"]`)
      );
      if (slideIndex === -1) return;
      sliderEl.swiper.slideTo(slideIndex, 0);
      sliderEl.swiper.update();
    };

    const attachMediaToggleHandlers = () => {
      section
        .querySelectorAll(".product__media-list .product__media-toggle")
        .forEach((elem) => {
          elem.addEventListener("click", onMainSliderToggleClick);
        });
    };

    if (sliderEl.swiper) {
      attachMediaToggleHandlers();
      return;
    }

    new Swiper(sliderEl, {
      slidesPerView: 1,
      spaceBetween: 8,
      speed: speed,
      loop: isLoop,
      zoom: {
        maxRatio: 2,
      },
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: buttonNext,
        prevEl: buttonPrev,
      },
      on: {
        init: function () {
          if (slideCountEl) {
            slideCountEl.textContent = `1/${this.slides.length}`;
          }
        },
        afterInit: attachMediaToggleHandlers,
        slideChange: function () {
          window.pauseAllMedia(sliderEl);
          this.params.noSwiping = false;
          sliderEl.classList.remove("zoom");

          if (slideCountEl) {
            const activeIndex = isLoop ? this.realIndex : this.activeIndex;
            slideCountEl.textContent = `${activeIndex + 1}/${
              this.slides.length
            }`;
          }
        },
        touchMove: function () {
          sliderEl.classList.remove("zoom");
        },
        slideChangeTransitionEnd: function () {
          const activeSlide = getActiveSwiperSlide(this);
          const model3D = activeSlide?.querySelector("model-viewer");
          const posterBtn3D = activeSlide?.querySelector(
            ".shopify-model-viewer-ui__button--poster"
          );
          if (model3D && posterBtn3D) {
            posterBtn3D.removeAttribute("hidden");
          }
        },
        touchStart: function () {
          const activeSlide = getActiveSwiperSlide(this);
          const model3D = activeSlide?.querySelector("model-viewer");
          if (model3D) {
            if (
              !model3D.classList.contains("shopify-model-viewer-ui__disabled")
            ) {
              this.params.noSwiping = true;
              this.params.noSwipingClass = "swiper-slide";
            } else {
              this.params.noSwiping = false;
            }
          }
        },
      },
    });
  };

  // Thumb-follow helper (shared by the stacked_previews + framed_stacked):
  // highlight the active thumb and reveal it in the rail.
  // The rail is driven by a TRANSFORM (the `--thumb-shift` var the desktop CSS applies to the track).
  const createThumbFollower = (thumbs, thumbEls, signal) => {
    const track = thumbs.querySelector(".product__media-sublist-container");
    let activeMediaId = null;
    let shift = 0;

    const getMaxShift = () => {
      const trackTop = track.getBoundingClientRect().top;
      // visually-lowest thumb (respects CSS `order`), not the DOM-last one
      const contentBottom =
        Math.max(...thumbEls.map((el) => el.getBoundingClientRect().bottom)) -
        trackTop;
      return Math.max(0, contentBottom - thumbs.clientHeight);
    };

    // animate = false → instant (used while dragging so the rail tracks 1:1).
    const applyShift = (value, animate) => {
      shift = Math.max(0, Math.min(getMaxShift(), value));
      track.style.transition = animate ? "" : "none";
      track.style.setProperty("--thumb-shift", `${-shift}px`);
    };

    if (track && signal) {
      let dragging = false;
      let startY = 0;
      let startShift = 0;
      let moved = false;

      thumbs.addEventListener(
        "pointerdown",
        (event) => {
          if (window.innerWidth < 990 || event.button !== 0) return;
          if (getMaxShift() <= 0) return;
          dragging = true;
          moved = false;
          startY = event.clientY;
          startShift = shift;
          event.preventDefault(); // no text selection / native image drag
          thumbs.classList.add("is-grabbing");
        },
        { signal }
      );

      // Listen on the document so a drag continues even if the pointer leaves
      // the rail; gated on `dragging` so it is a no-op otherwise.
      document.addEventListener(
        "pointermove",
        (event) => {
          if (!dragging) return;
          const dy = event.clientY - startY;
          if (Math.abs(dy) > 3) moved = true;
          applyShift(startShift - dy, false); // grab-and-drag: content follows
        },
        { signal }
      );

      const endDrag = () => {
        if (!dragging) return;
        dragging = false;
        thumbs.classList.remove("is-grabbing");
        track.style.transition = "";
      };
      document.addEventListener("pointerup", endDrag, { signal });
      document.addEventListener("pointercancel", endDrag, { signal });

      // Swallow the click that follows a real drag so it doesn't jump the media.
      thumbs.addEventListener(
        "click",
        (event) => {
          if (!moved) return;
          moved = false;
          event.stopPropagation();
          event.preventDefault();
        },
        { signal, capture: true }
      );
    }

    // Mark one thumb active and reveal it in the rail ONLY if it is not already
    // fully visible (otherwise the rail stays put — nothing jumps).
    const setActive = (mediaId) => {
      const activeThumb = thumbs.querySelector(
        `[data-media-sub-id="${mediaId}"]`
      );
      if (!activeThumb || !track) return;

      activeMediaId = mediaId;

      thumbEls.forEach((thumb) => {
        thumb.classList.toggle("active", thumb === activeThumb);
      });

      const trackTop = track.getBoundingClientRect().top;
      const view = thumbs.clientHeight;
      const top = activeThumb.getBoundingClientRect().top - trackTop;
      const bottom = top + activeThumb.offsetHeight;

      let next = shift;
      if (top < shift) {
        next = top;
      } else if (bottom > shift + view) {
        next = bottom - view;
      }
      applyShift(next, true);
    };

    // A click on a thumb (or its programmatic scroll) locks the active thumb:
    // the gallery smooth-scrolls to that media and the scroll-spy would
    // otherwise light up every thumb it passes ("thumbs jump"). While locked the
    // spy is frozen on the chosen thumb; it releases once the gallery arrives at
    // that media or after a safety timeout.
    let lockedId = null;
    let lockedUntil = 0;

    // spy (scroll-driven): honours the lock, idempotent on the active id
    const follow = (mediaId) => {
      if (lockedId) {
        if (mediaId === lockedId || Date.now() > lockedUntil) {
          lockedId = null; // arrived at the target, or lock expired → resume
        } else {
          return; // still travelling → keep the clicked thumb active
        }
      }
      if (mediaId === activeMediaId) return;
      setActive(mediaId);
    };

    // click-driven: pin this one thumb and freeze the spy for the scroll
    const select = (mediaId) => {
      lockedId = mediaId;
      lockedUntil = Date.now() + 900;
      setActive(mediaId);
    };

    return { follow, select };
  };

  // Active media = the last one (in VISUAL order) whose top has passed the
  // reading line. Sort by rendered top so a CSS `order`-pinned item (the
  // variant-featured media) is honoured; DOM order can differ from what's shown.
  // Clamp the extremes so a first/last item shorter than the reading-line offset
  // still pins when the scroll is at that edge.
  const findActiveMedia = (mediaEls, readingLine, atTop, atBottom) => {
    const ordered = mediaEls
      .map((el) => ({ el, top: el.getBoundingClientRect().top }))
      .sort((a, b) => a.top - b.top);
    if (atTop) return ordered[0].el;
    if (atBottom) return ordered[ordered.length - 1].el;
    let active = ordered[0].el;
    for (const { el, top } of ordered) {
      if (top - 1 <= readingLine) {
        active = el;
      } else {
        break;
      }
    }
    return active;
  };

  // stacked_previews: the whole page scrolls; the thumb rail is a scroll-spy on
  // the window that follows which stacked image sits on the reading line.
  const initStackedPreviews = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    if (section._stackedPreviewsCleanup) {
      section._stackedPreviewsCleanup();
      section._stackedPreviewsCleanup = null;
    }

    const mediaList = section.querySelector(
      ".product__media-list[data-desktop-type='stacked_previews']"
    );
    const thumbs = section.querySelector(
      ".product__media-sublist[data-desktop-type='stacked_previews']"
    );
    if (!mediaList || !thumbs) return;

    const mediaEls = Array.from(
      mediaList.querySelectorAll(".product__media-item[data-media-id]")
    );
    const thumbEls = Array.from(thumbs.querySelectorAll("[data-media-sub-id]"));
    if (mediaEls.length <= 1) return;

    const controller = new AbortController();
    const { signal } = controller;
    const follower = createThumbFollower(thumbs, thumbEls, signal);

    const onScroll = () => {
      if (window.innerWidth < 990) return;
      const scrollEl = document.scrollingElement || document.documentElement;
      const maxScroll = scrollEl.scrollHeight - window.innerHeight;
      const atTop = window.scrollY <= 1;
      const atBottom = window.scrollY >= maxScroll - 1;
      const active = findActiveMedia(
        mediaEls,
        window.innerHeight / 2,
        atTop,
        atBottom
      );
      if (active) follower.follow(active.dataset.mediaId);
    };

    requestAnimationFrame(onScroll);
    window.addEventListener("scroll", onScroll, { passive: true, signal });

    thumbEls.forEach((thumb) => {
      thumb.addEventListener(
        "click",
        () => {
          if (window.innerWidth < 990) return;
          const mediaId = thumb.dataset.mediaSubId;
          const targetMedia = mediaList.querySelector(
            `[data-media-id="${mediaId}"]`
          );
          if (!targetMedia) return;
          const headerOffset =
            parseFloat(
              getComputedStyle(document.documentElement).getPropertyValue(
                "--header-height"
              )
            ) || 0;
          const offset =
            targetMedia.getBoundingClientRect().top + window.scrollY;
          // Pin + lock this thumb BEFORE scrolling so the spy stays frozen on it.
          follower.select(mediaId);
          window.scrollTo({
            top: Math.max(0, offset - headerOffset - 8),
            behavior: "smooth",
          });
        },
        { signal }
      );
    });

    section._stackedPreviewsCleanup = () => controller.abort();
  };

  // framed_stacked: the media column is a fixed-height frame with its own
  // scroll. We hijack the wheel over the media to drive that inner scroll (and
  // release it to the page at the edges) while the thumb rail follows along.
  const initFramedStacked = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    if (section._framedStackedCleanup) {
      section._framedStackedCleanup();
      section._framedStackedCleanup = null;
    }

    const mediaList = section.querySelector(
      ".product__media-list[data-desktop-type='framed_stacked']"
    );
    const thumbs = section.querySelector(
      ".product__media-sublist[data-desktop-type='framed_stacked']"
    );
    if (!mediaList || !thumbs) return;

    const scrollRoot = mediaList.closest(".product__media-list-wrapper");
    const mediaCol = section.querySelector(".product__media");
    if (!scrollRoot || !mediaCol) return;

    const mediaEls = Array.from(
      mediaList.querySelectorAll(".product__media-item[data-media-id]")
    );
    const thumbEls = Array.from(thumbs.querySelectorAll("[data-media-sub-id]"));
    if (mediaEls.length <= 1) return;

    const controller = new AbortController();
    const { signal } = controller;
    const follower = createThumbFollower(thumbs, thumbEls, signal);

    const isPointInRect = (x, y, rect) =>
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

    const onScroll = () => {
      if (window.innerWidth < 990) return;
      const rect = scrollRoot.getBoundingClientRect();
      const maxScroll = scrollRoot.scrollHeight - scrollRoot.clientHeight;
      const atTop = scrollRoot.scrollTop <= 1;
      const atBottom = scrollRoot.scrollTop >= maxScroll - 1;
      const active = findActiveMedia(
        mediaEls,
        rect.top + rect.height / 2,
        atTop,
        atBottom
      );
      if (active) follower.follow(active.dataset.mediaId);
    };

    requestAnimationFrame(onScroll);
    scrollRoot.addEventListener("scroll", onScroll, { passive: true, signal });

    const onWheel = (event) => {
      if (window.innerWidth < 990) return;

      const { clientX, clientY, deltaY } = event;
      if (!isPointInRect(clientX, clientY, mediaCol.getBoundingClientRect())) {
        return;
      }
      // Let the thumb rail scroll natively when the pointer is over it.
      if (isPointInRect(clientX, clientY, thumbs.getBoundingClientRect())) {
        return;
      }

      const maxScroll = scrollRoot.scrollHeight - scrollRoot.clientHeight;
      if (maxScroll <= 0) return;

      const atTop = scrollRoot.scrollTop <= 0;
      const atBottom = scrollRoot.scrollTop >= maxScroll - 1;

      // Drive the inner scroll while it can still move in the wheel direction;
      // at the edges release the wheel so the page scrolls on past the frame.
      if ((deltaY > 0 && !atBottom) || (deltaY < 0 && !atTop)) {
        event.preventDefault();
        scrollRoot.scrollTop = Math.max(
          0,
          Math.min(maxScroll, scrollRoot.scrollTop + deltaY)
        );
        onScroll();
      }
    };

    section.addEventListener("wheel", onWheel, { passive: false, signal });

    thumbEls.forEach((thumb) => {
      thumb.addEventListener(
        "click",
        () => {
          if (window.innerWidth < 990) return;
          const mediaId = thumb.dataset.mediaSubId;
          const targetMedia = mediaList.querySelector(
            `[data-media-id="${mediaId}"]`
          );
          if (!targetMedia) return;
          const rootRect = scrollRoot.getBoundingClientRect();
          const mediaRect = targetMedia.getBoundingClientRect();
          const top = scrollRoot.scrollTop + (mediaRect.top - rootRect.top);
          // Pin + lock this thumb BEFORE scrolling so the spy stays frozen on it.
          follower.select(mediaId);
          scrollRoot.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        },
        { signal }
      );
    });

    section._framedStackedCleanup = () => controller.abort();
  };

  const initProductAccordion = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const accordions = section.querySelectorAll(".product-accordion");

    accordions.forEach((accordion) => {
      accordion.addEventListener("click", (event) => {
        const toggleEl = event.target.closest(".product-accordion__toggle");
        if (!toggleEl) return;

        const contentEl = toggleEl.nextElementSibling;
        if (
          !contentEl ||
          !contentEl.classList.contains("product-accordion__content")
        ) {
          return;
        }

        const isActive = toggleEl.classList.contains("active");
        if (!isActive) {
          slideDown(toggleEl, contentEl, 300); // func in global.js
        } else {
          slideUp(toggleEl, contentEl, 300); // func in global.js
        }
      });
    });
  };

  const initStickyAddBar = (section) => {
    if (!section || !section.classList.contains("product-section")) return;

    const buyButtons = section.querySelector(
      ".product__buy-buttons > product-form"
    );
    const stickyBar = section.querySelector(".product-sticky-add-bar");
    const footerBottom = document.querySelector(
      ".shopify-section-group-footer-group .footer-bottom"
    );

    if (!stickyBar) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.01,
    };

    const isElementBelowScroll = (element) => {
      return element ? element.getBoundingClientRect().top > 0 : false;
    };

    const toggleStickyBar = (isVisible) => {
      if (isVisible || isElementBelowScroll(buyButtons)) {
        stickyBar.classList.remove("active");
      } else {
        stickyBar.classList.add("active");
      }
    };

    const handleIntersect = (entries) => {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      toggleStickyBar(isVisible);
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    if (buyButtons) observer.observe(buyButtons);
    if (footerBottom) observer.observe(footerBottom);
  };

  let isZoomCursorInit = false;
  const initZoomCursor = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const mediaList = section.querySelector(".product__media-list");
    const mediaListWrapper = section.querySelector(
      ".product__media-list-wrapper"
    );
    if (!mediaList || !mediaListWrapper) return;

    const hasZoom = mediaList.getAttribute("data-has-desktop-zoom") === "true";
    if (!hasZoom) return;

    const cursorEl = mediaListWrapper.querySelector(".product__media-cursor");
    if (!cursorEl) return;

    let x = 0;
    let y = 0;
    let targetX = 0;
    let targetY = 0;
    let raf = null;
    let positioned = false;
    let lastClientX = null;
    let lastClientY = null;

    // Fold the wrapper's scrollTop in so the cursor stays under the pointer
    // even when the media column scrolls (stacked-with-preview). scrollTop is 0
    // for the non-scrolling gallery types, so this is harmless there.
    const setTargets = () => {
      const rect = mediaListWrapper.getBoundingClientRect();
      targetX = lastClientX - rect.left;
      targetY = lastClientY - rect.top + mediaListWrapper.scrollTop;
    };

    const tick = () => {
      const ease = 0.18;
      x += (targetX - x) * ease;
      y += (targetY - y) * ease;
      cursorEl.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };

    const show = () => {
      cursorEl.classList.add("is-visible");
      mediaListWrapper.classList.add("modal-cursor-shown");
    };

    const hide = () => {
      cursorEl.classList.remove("is-visible");
      mediaListWrapper.classList.remove("modal-cursor-shown");
    };

    const handleMouseMove = (e) => {
      lastClientX = e.clientX;
      lastClientY = e.clientY;
      setTargets();

      if (e.target.closest(".product__media-toggle")) {
        if (!positioned) {
          x = targetX;
          y = targetY;
          positioned = true;
        }
        show();
      } else {
        hide();
      }
    };

    const handleScroll = () => {
      if (lastClientX == null) return;
      setTargets();
      x = targetX;
      y = targetY;
    };

    const handleMouseLeave = () => {
      hide();
      positioned = false;
    };

    const enable = () => {
      if (isZoomCursorInit) return;
      mediaListWrapper.addEventListener("mousemove", handleMouseMove);
      mediaListWrapper.addEventListener("mouseleave", handleMouseLeave);
      mediaListWrapper.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      raf = requestAnimationFrame(tick);
      isZoomCursorInit = true;
    };

    const disable = () => {
      if (!isZoomCursorInit) return;
      mediaListWrapper.removeEventListener("mousemove", handleMouseMove);
      mediaListWrapper.removeEventListener("mouseleave", handleMouseLeave);
      mediaListWrapper.removeEventListener("scroll", handleScroll);
      if (raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      hide();
      cursorEl.style.transform = "translate3d(-100vw, -100vh, 0)";
      positioned = false;
      isZoomCursorInit = false;
    };

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.contentRect.width >= 990 &&
          window.matchMedia("screen and (hover: hover) and (pointer: fine)")
            .matches
        ) {
          enable();
        } else {
          disable();
        }
      });
    });

    resizeObserver.observe(section);
  };

  const initSliderCursorArrows = (section) => {
    if (!section || !section.classList.contains("product-section")) {
      return;
    }

    const mediaList = section.querySelector(".js-media-list");
    if (!mediaList || mediaList.dataset?.desktopType !== "slider") return;

    const arrowsWrap = section.querySelector(".product__media-arrows");
    if (!arrowsWrap) return;

    const zone = arrowsWrap.querySelector(".product__media-arrow-zone");
    const staticArrows = arrowsWrap.querySelectorAll(
      ".product__media-arrow-static"
    );
    if (!zone && !staticArrows.length) return;

    const getSwiper = () => mediaList.swiper;

    // Slider mode presents zoom as the active slide's corner toggle. These let
    // the arrows overlay redirect a corner click to zoom instead of paging.
    const getActiveZoomToggle = () =>
      getActiveSwiperSlide(getSwiper())?.querySelector(
        ".product__media-toggle"
      );

    const pointInZoomToggle = (x, y) => {
      const toggle = getActiveZoomToggle();
      if (!toggle) return false;
      const r = toggle.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };

    // Loop / rewind always wrap, so only a finite gallery can reach an inert end.
    const isEndReached = (direction) => {
      const swiper = getSwiper();
      if (!swiper || swiper.params.loop || swiper.params.rewind) return false;
      return direction === "prev" ? swiper.isBeginning : swiper.isEnd;
    };

    const navigate = (direction) => {
      const swiper = getSwiper();
      if (!swiper) return;
      if (direction === "next") {
        swiper.slideNext();
      } else {
        swiper.slidePrev();
      }
      syncEnds();
    };

    const nav =
      zone && typeof window.initCursorArrowZone === "function"
        ? window.initCursorArrowZone(zone, {
            onNavigate: (direction, event) => {
              // A click on the active slide's zoom toggle opens zoom instead of paging.
              if (pointInZoomToggle(event.clientX, event.clientY)) {
                getActiveZoomToggle()?.click();
                return;
              }
              navigate(direction);
            },
            isEndReached,
            // Over the zoom corner the nav pill drops and the cursor turns back into a pointer,
            // so the click reads as "zoom", not "page".
            isSuppressed: pointInZoomToggle,
          })
        : null;

    const syncEnds = () => {
      staticArrows.forEach((button) => {
        button.classList.toggle(
          "is-disabled",
          isEndReached(button.dataset.direction)
        );
      });
      nav?.sync();
    };

    staticArrows.forEach((button) => {
      if (button.dataset.arrowNavInit === "true") return;
      button.dataset.arrowNavInit = "true";
      button.addEventListener("click", () => {
        if (button.classList.contains("is-disabled")) return;
        navigate(button.dataset.direction);
      });
    });

    const swiper = getSwiper();
    if (swiper) {
      swiper.on("slideChange", syncEnds);
      swiper.on("slideChange", () => syncMediaArrowsInteractiveState(section));
    }
    syncEnds();
    syncMediaArrowsInteractiveState(section);
  };

  initSubSlider(section);
  initMainSlider(section);
  initZoomSlider(section);
  initStackedPreviews(section);
  initFramedStacked(section);

  initStickyAddBar(section);
  initZoomCursor(section);
  initSliderCursorArrows(section);
  initProductAccordion(section);

  document.addEventListener("shopify:section:load", (event) => {
    initSubSlider(event.target);
    initMainSlider(event.target);
    initZoomSlider(event.target);
    initStackedPreviews(event.target);
    initFramedStacked(event.target);

    initStickyAddBar(event.target);
    initZoomCursor(event.target);
    initSliderCursorArrows(event.target);
    if (!window.Shopify.designMode) {
      initProductAccordion(event.target);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target._stackedPreviewsCleanup) {
      event.target._stackedPreviewsCleanup();
      event.target._stackedPreviewsCleanup = null;
    }
    if (event.target._framedStackedCleanup) {
      event.target._framedStackedCleanup();
      event.target._framedStackedCleanup = null;
    }
  });
}

initProductPage(document.currentScript.parentElement);
